// src/pages/FormationDetailPage.js

import React, { useContext, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ThemeContext } from '../context/ThemeContext';
import { formationService } from '../api';
import Button from '../components/common/Button';
import SocialShare from '../components/common/SocialShare';
import PageLoader from '../components/common/PageLoader';
import ErrorDisplay from '../components/common/ErrorDisplay';

// Styled components remain the same
const FormationSection = styled.section`
  padding: 5rem 2rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Breadcrumbs = styled.div`
  margin-bottom: 2rem;
  color: ${props => props.theme.text};
  
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  span {
    margin: 0 0.5rem;
  }
`;

const FormationGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const FormationContent = styled.div``;

const FormationImage = styled.div`
  width: 100%;
  height: 400px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

const FormationTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.text};
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const OnlineTag = styled.span`
  display: inline-block;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  color: white;
  background-color: ${props => props.theme.primary};
  margin-left: 1rem;
  vertical-align: middle;
`;

const FormationMetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin: 1.5rem 0;
  color: ${props => props.theme.text};
  
  div {
    display: flex;
    align-items: center;
    
    i {
      margin-right: 0.5rem;
      color: ${props => props.theme.primary};
    }
  }
`;

const FormationDescription = styled.div`
  color: ${props => props.theme.text};
  line-height: 1.8;
  margin-bottom: 2rem;
  
  p {
    margin-bottom: 1.5rem;
  }
  
  h2 {
    font-size: 1.8rem;
    margin: 2rem 0 1rem;
    color: ${props => props.theme.primary};
  }
  
  h3 {
    font-size: 1.5rem;
    margin: 1.8rem 0 1rem;
  }
  
  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 2rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
`;

const FormationSidebar = styled.div``;

const SidebarCard = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const SidebarTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.primary};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -8px;
    width: 50px;
    height: 2px;
    background-color: ${props => props.theme.accent};
  }
`;

const PriceTag = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.primary};
  margin-bottom: 1rem;
  
  .unit {
    font-size: 1rem;
    opacity: 0.7;
  }
`;

const FormationStats = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.text};
`;

const StatItem = styled.div`
  text-align: center;
  
  .number {
    font-size: 1.5rem;
    font-weight: bold;
    color: ${props => props.theme.primary};
    margin-bottom: 0.3rem;
  }
  
  .label {
    font-size: 0.9rem;
    opacity: 0.8;
  }
`;

const InstructorCard = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const InstructorImage = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-image: url(${props => props.src || '/default-avatar.jpg'});
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  border: 2px solid ${props => props.theme.primary};
`;

const InstructorInfo = styled.div`
  flex: 1;
`;

const InstructorName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.3rem;
  color: ${props => props.theme.text};
`;

const InstructorRole = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.text};
  opacity: 0.8;
  margin-bottom: 0.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.text};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-family: 'Open Sans', sans-serif;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.8rem;
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-family: 'Open Sans', sans-serif;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.error};
  font-size: 0.9rem;
  margin-top: 0.3rem;
`;

const SuccessMessage = styled(motion.div)`
  background-color: ${props => props.theme.success};
  color: white;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const MapContainer = styled.div`
  height: 200px;
  margin-top: 1rem;
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 4px;
  }
`;

const LoginPrompt = styled.div`
  text-align: center;
  padding: 1rem;
  background: ${props => props.theme.backgroundLight};
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const FormationDetailPage = () => {
  const { formationSlug } = useParams();
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  
  const [formation, setFormation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [participantCount, setParticipantCount] = useState(0);
  
  // Chargement initial des données de formation
  useEffect(() => {
    const fetchFormation = async () => {
      try {
        setLoading(true);
        // Vérifier si formationSlug est un numéro ou une chaîne
        const isNumeric = !isNaN(formationSlug);
        let response;
        
        if (isNumeric) {
          // Si c'est un ID numérique
          response = await formationService.getById(formationSlug);
        } else {
          // Si c'est un slug texte
          response = await formationService.getById(formationSlug);
        }
        
        console.log("Formation data:", response.data);
        setFormation(response.data);
        
        // Compter les participants directement à partir des données de formation
        const participantsCount = response.data.participants ? response.data.participants.length : 0;
        
        // Nous n'utilisons pas formationRegistrationService car il nécessite des droits d'administrateur
        // Utiliser directement le nombre de participants fourni par l'API de formation
        setParticipantCount(participantsCount);
        
        // Mise à jour du titre de la page
        document.title = `${response.data.title} | PropentaTech`;
      } catch (err) {
        console.error("Error fetching formation details:", err);
        setError(err.response?.data?.detail || t('errorLoadingFormation'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchFormation();
    
    return () => {
      document.title = 'PropentaTech';
    };
  }, [formationSlug, t]);
  
  // Validation schema pour le formulaire d'inscription
  const validationSchema = Yup.object({
    name: Yup.string().required(t('nameRequired')),
    email: Yup.string().email(t('invalidEmail')).required(t('emailRequired')),
    phone: Yup.string().required(t('phoneRequired')),
    motivation: Yup.string().max(500, t('motivationTooLong'))
  });
  
  // Configuration du formulaire
  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      motivation: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        
        // Préparation des données d'inscription
        const registrationData = {
          name: values.name,
          email: values.email,
          phone: values.phone,
          motivation: values.motivation
        };
        
        // Appel à l'API pour s'inscrire à la formation en utilisant l'endpoint register
        await formationService.register(formation.id, registrationData);
        
        // Mettre à jour le nombre de participants localement
        setParticipantCount(prevCount => prevCount + 1);
        
        setRegistrationSuccess(true);
        formik.resetForm();
        
        // Masquer le message de succès après quelques secondes
        setTimeout(() => {
          setRegistrationSuccess(false);
        }, 8000);
      } catch (err) {
        console.error("Error registering for formation:", err);
        setError(err.response?.data?.detail || t('registrationFailed'));
      } finally {
        setIsSubmitting(false);
      }
    }
  });
  
  // Formatage de la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(navigator.language, options);
  };
  
  // Formatage du prix
  const formatPrice = (price) => {
    if (price === 0) return t('free');
    if (!price) return t('contactForPricing');
    return `${price.toLocaleString()} FCFA`;
  };
  
  // Calcul des places restantes
  const getRemainingSpots = () => {
    if (!formation) return 0;
    return Math.max(0, formation.max_participants - participantCount);
  };
  
  // Vérifier si la formation est complète
  const isFormationFull = () => {
    return getRemainingSpots() <= 0;
  };
  
  // Vérifier si la formation est terminée
  const isFormationEnded = () => {
    if (!formation || !formation.end_date) return false;
    const endDate = new Date(formation.end_date);
    return endDate < new Date();
  };
  
  // Vérifier si l'inscription est possible
  const canRegister = () => {
    return !isFormationFull() && !isFormationEnded();
  };
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (error) {
    return (
      <ErrorDisplay 
        error={error} 
        backLink="/formations" 
        backLinkText={t('backToFormations')} 
      />
    );
  }
  
  if (!formation) return null;
  
  return (
    <FormationSection>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Breadcrumbs theme={theme}>
            <Link to="/">{t('home')}</Link> <span>/</span> 
            <Link to="/formations">{t('formations')}</Link> <span>/</span> 
            <span>{formation.title}</span>
          </Breadcrumbs>

          <FormationTitle theme={theme}>
            {formation.title}
            {formation.is_online && (
              <OnlineTag theme={theme}>{t('online')}</OnlineTag>
            )}
          </FormationTitle>

          <FormationGrid>
            <FormationContent>
              {formation.image && (
                <FormationImage src={formation.image} />
              )}
              
              <FormationMetaInfo theme={theme}>
                <div>
                  <i className="far fa-calendar-alt"></i>
                  <span>
                    {formatDate(formation.start_date)}
                    {formation.end_date && formation.end_date !== formation.start_date && 
                      ` - ${formatDate(formation.end_date)}`}
                  </span>
                </div>
                <div>
                  <i className="fas fa-map-marker-alt"></i>
                  <span>
                    {formation.is_online ? t('onlineFormation') : formation.location}
                  </span>
                </div>
                {formation.instructor && (
                  <div>
                    <i className="fas fa-user"></i>
                    <span>
                      {formation.instructor.first_name} {formation.instructor.last_name}
                    </span>
                  </div>
                )}
                <div>
                  <i className="fas fa-users"></i>
                  <span>
                    {participantCount} / {formation.max_participants} {t('participants')}
                  </span>
                </div>
              </FormationMetaInfo>
              
              <SocialShare 
                title={formation.title}
                hashtags={['PropentaTech', 'Formation', formation.title.replace(/\s+/g, '')]}
              />
              
              <FormationDescription theme={theme} dangerouslySetInnerHTML={{ __html: formation.description }} />
            </FormationContent>

            <FormationSidebar>
              <SidebarCard theme={theme}>
                <SidebarTitle theme={theme}>{t('formationDetails')}</SidebarTitle>
                
                <FormationStats theme={theme}>
                  <StatItem theme={theme}>
                    <div className="number">{formatDate(formation.start_date).split(' ')[0]}</div>
                    <div className="label">{t('startDate')}</div>
                  </StatItem>
                  
                  {formation.end_date && (
                    <StatItem theme={theme}>
                      <div className="number">{formatDate(formation.end_date).split(' ')[0]}</div>
                      <div className="label">{t('endDate')}</div>
                    </StatItem>
                  )}
                  
                  <StatItem theme={theme}>
                    <div className="number">{getRemainingSpots()}</div>
                    <div className="label">{t('spotsLeft')}</div>
                  </StatItem>
                </FormationStats>
                
                <PriceTag theme={theme}>
                  {formatPrice(formation.price)}
                  {formation.price > 0 && <span className="unit">FCFA</span>}
                </PriceTag>
                
                {!canRegister() && (
                  <div style={{ 
                    marginBottom: '1.5rem', 
                    color: theme.error,
                    padding: '0.5rem 1rem',
                    background: `${theme.error}20`,
                    borderRadius: '4px'
                  }}>
                    {isFormationFull() ? t('formationFull') : t('formationEnded')}
                  </div>
                )}
                
                {formation.instructor && (
                  <>
                    <SidebarTitle theme={theme}>{t('instructor')}</SidebarTitle>
                    <InstructorCard>
                      <InstructorImage src={formation.instructor.profile_picture} theme={theme} />
                      <InstructorInfo>
                        <InstructorName theme={theme}>
                          {formation.instructor.first_name} {formation.instructor.last_name}
                        </InstructorName>
                        <InstructorRole theme={theme}>
                          {formation.instructor.position || t('formationInstructor')}
                        </InstructorRole>
                        <div style={{ fontSize: '0.9rem', color: theme.text, opacity: 0.8 }}>
                          {formation.instructor.bio ? 
                            `${formation.instructor.bio.substring(0, 120)}...` : 
                            t('noInstructorBio')}
                        </div>
                      </InstructorInfo>
                    </InstructorCard>
                  </>
                )}
                
                {!formation.is_online && formation.location && (
                  <>
                    <SidebarTitle theme={theme}>{t('location')}</SidebarTitle>
                    <p style={{ color: theme.text, marginBottom: '0.5rem' }}>
                      {formation.location}
                    </p>
                    <MapContainer>
                      <iframe 
                        src={`https://www.google.com/maps/embed/v1/place?q=${encodeURIComponent(formation.location)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
                        title="Formation Location"
                        allowFullScreen=""
                      ></iframe>
                    </MapContainer>
                  </>
                )}
              </SidebarCard>
              
              <SidebarCard theme={theme}>
                <SidebarTitle theme={theme}>{t('registerForFormation')}</SidebarTitle>
                
                {registrationSuccess ? (
                  <SuccessMessage
                    theme={theme}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3 style={{ marginBottom: '0.5rem' }}>{t('registrationSuccess')}</h3>
                    <p>{t('registrationSuccessDesc')}</p>
                  </SuccessMessage>
                ) : (
                  <>
                    {canRegister() ? (
                      <form onSubmit={formik.handleSubmit}>
                        <FormGroup>
                          <Label htmlFor="name" theme={theme}>{t('fullName')}</Label>
                          <Input
                            type="text"
                            id="name"
                            name="name"
                            theme={theme}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.name}
                          />
                          {formik.touched.name && formik.errors.name && (
                            <ErrorMessage theme={theme}>{formik.errors.name}</ErrorMessage>
                          )}
                        </FormGroup>
                        
                        <FormGroup>
                          <Label htmlFor="email" theme={theme}>{t('email')}</Label>
                          <Input
                            type="email"
                            id="email"
                            name="email"
                            theme={theme}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.email}
                          />
                          {formik.touched.email && formik.errors.email && (
                            <ErrorMessage theme={theme}>{formik.errors.email}</ErrorMessage>
                          )}
                        </FormGroup>
                        
                        <FormGroup>
                          <Label htmlFor="phone" theme={theme}>{t('phone')}</Label>
                          <Input
                            type="tel"
                            id="phone"
                            name="phone"
                            theme={theme}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.phone}
                          />
                          {formik.touched.phone && formik.errors.phone && (
                            <ErrorMessage theme={theme}>{formik.errors.phone}</ErrorMessage>
                          )}
                        </FormGroup>
                        
                        <FormGroup>
                          <Label htmlFor="motivation" theme={theme}>
                            {t('motivation')} ({t('optional')})
                          </Label>
                          <TextArea
                            id="motivation"
                            name="motivation"
                            theme={theme}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.motivation}
                            placeholder={t('motivationOptionalPlaceholder')}
                          />
                          {formik.touched.motivation && formik.errors.motivation && (
                            <ErrorMessage theme={theme}>{formik.errors.motivation}</ErrorMessage>
                          )}
                        </FormGroup>
                        
                        <Button
                          type="submit"
                          variant="primary"
                          size="large"
                          style={{ width: '100%' }}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                              {t('processing')}
                            </>
                          ) : (
                            t('registerNow')
                          )}
                        </Button>
                      </form>
                    ) : (
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ color: theme.text, marginBottom: '1.5rem' }}>
                          {isFormationFull() ? 
                            t('formationFullMessage') : 
                            t('formationEndedMessage')}
                        </p>
                        <Button
                          variant="outline"
                          size="large"
                          to="/formations"
                        >
                          {t('exploreOtherFormations')}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </SidebarCard>
            </FormationSidebar>
          </FormationGrid>
        </motion.div>
      </Container>
    </FormationSection>
  );
};

export default FormationDetailPage;